import { css, CSSObject } from '@emotion/core'
import facepaint from 'facepaint'
import { BREAKPOINTS } from './layout'

/**
 * regular: `400`,
 * bold: `700`,
 */
export const fontWeight = {
  regular: { fontWeight: 400 },
  bold: { fontWeight: 700 },
}

export const sourceSerifBold = {
  name: 'Source Serif Bold',
  fontFamily: 'var(--font-serif, var(--font-title))',
  fallback: ['sans-serif'],
  cssProperties: {
    ...fontWeight.bold,
    fontStyle: 'normal',
  },
}

export const plexRegular = {
  name: 'Plex Regular',
  fontFamily: 'var(--font-sans-serif)',
  fallback: ['sans-serif'],
  cssProperties: {
    ...fontWeight.regular,
    fontStyle: 'normal',
  },
}

export const plexBold = {
  name: 'Plex Bold',
  fontFamily: 'var(--font-sans-serif)',
  fallback: ['sans-serif'],
  cssProperties: {
    ...fontWeight.bold,
    fontStyle: 'normal',
  },
}

export const mq: (...styles: CSSObject[]) => CSSObject = facepaint(
  BREAKPOINTS.slice(1).map(([px]) => `@media screen and (min-width: ${px}px)`),
)

export const mkFont = (fontProps: any, cssProperties?: CSSObject) => {
  return css`
    label: font ${fontProps.name};

    ${mq({
      fontFamily: [fontProps.fontFace.fontFamily, ...fontProps.fontFace.fallback].join(', '),
      ...fontProps.fontFace.cssProperties,
      ...fontProps.cssProperties,
      ...cssProperties,
    } as CSSObject)}
  `
}

/*
 * Basic
 */

export const fontHeading0 = mkFont(
  {
    name: 'Heading0',
    fontFace: sourceSerifBold,
    cssProperties: {
      fontSize: '64px',
      lineHeight: '64px',
      textIndent: '-0.05em',
    },
  },
  {
    fontSize: ['28px', '28px', '48px', '64px'],
    lineHeight: ['28px', '28px', '48px', '64px'],
  },
)

export const fontHeading1 = mkFont(
  {
    name: 'Heading1',
    fontFace: sourceSerifBold,
    cssProperties: {
      fontSize: '60px',
      lineHeight: '65px',
      textIndent: '-0.05em',
    },
  },
  {
    fontSize: ['28px', '28px', '36px', '60px'],
    lineHeight: ['32px', '32px', '45px', '65px'],
  },
)

export const fontHeading15 = mkFont(
  {
    name: 'Heading15',
    fontFace: sourceSerifBold,
    cssProperties: {
      fontSize: '48px',
      lineHeight: '51px',
      textIndent: '-0.05em',
    },
  },
  {
    fontSize: ['28px', '28px', '32px', '48px'],
    lineHeight: ['32px', '32px', '40px', '51px'],
  },
)

export const fontHeading2 = mkFont(
  {
    name: 'Heading2',
    fontFace: sourceSerifBold,
    cssProperties: {
      fontSize: '36px',
      lineHeight: '45px',
      textIndent: '-0.05em',
    },
  },
  {
    fontSize: ['20px', '20px', '20px', '36px'],
    lineHeight: ['24px', '24px', '24px', '45px'],
  },
)

export const fontHeading3 = mkFont(
  {
    name: 'Heading3',
    fontFace: sourceSerifBold,
    cssProperties: {
      fontSize: '20px',
      lineHeight: '24px',
    },
  },
  {
    fontSize: ['18px', '18px', '18px', '20px'],
    lineHeight: ['20px', '20px', '20px', '24px'],
  },
)

export const fontLead = mkFont(
  {
    name: 'Lead',
    fontFace: plexRegular,
    cssProperties: {
      fontSize: '25px',
      lineHeight: '35px',
    },
  },
  {
    fontSize: ['18px', '18px', '18px', '25px'],
    lineHeight: ['20px', '20px', '20px', '35px'],
  },
)

export const fontByline = mkFont(
  {
    name: 'Byline',
    fontFace: plexRegular,
    cssProperties: {
      fontSize: '15px',
      lineHeight: '20px',
    },
  },
  {
    fontSize: ['12px', '12px', '12px', '15px'],
    lineHeight: ['16px', '16px', '16px', '20px'],
  },
)

export const fontChartAxis = mkFont(
  {
    name: 'ChartAxis',
    fontFace: plexRegular,
    cssProperties: {
      fontSize: '16px',
      lineHeight: '20px',
    },
  },
  {
    fontSize: ['12px', '12px', '14px', '16px'],
    lineHeight: ['16px', '16px', '18px', '20px'],
  },
)

export const fontLabelBold = mkFont(
  {
    name: 'LabelBold',
    fontFace: plexBold,
    cssProperties: {
      fontSize: '16px',
      lineHeight: '20px',
    },
  },
  {
    fontSize: ['16px', '16px', '16px', '24px'],
    lineHeight: ['20px', '20px', '20px', '32px'],
  },
)

export const fontCaption = mkFont(
  {
    name: 'Caption',
    fontFace: plexRegular,
    cssProperties: {
      fontSize: '16px',
      lineHeight: '20px',
    },
  },
  {
    fontSize: ['12px', '12px', '14px', '16px'],
    lineHeight: ['16px', '16px', '18px', '20px'],
  },
)

/*
 * Fixed-size
 */

export const font20Regular = mkFont({
  name: '20Regular',
  fontFace: plexRegular,
  cssProperties: {
    fontSize: '20px',
    lineHeight: '24px',
    letterSpacing: '0.01em',
  },
})

export const font18Regular = mkFont({
  name: '18Regular',
  fontFace: plexRegular,
  cssProperties: {
    fontSize: '18px',
    lineHeight: '22px',
    letterSpacing: '0.01em',
  },
})

export const font18Bold = mkFont({
  name: '18Bold',
  fontFace: plexBold,
  cssProperties: {
    fontSize: '18px',
    lineHeight: '22px',
    letterSpacing: '0.01em',
  },
})

export const font16Bold = mkFont({
  name: '16Bold',
  fontFace: plexBold,
  cssProperties: {
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.01em',
  },
})

export const font16Regular = mkFont({
  name: '16Regular',
  fontFace: plexRegular,
  cssProperties: {
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.01em',
  },
})

export const font15Regular = mkFont({
  name: '15Regular',
  fontFace: plexRegular,
  cssProperties: {
    fontSize: '15px',
    lineHeight: '20px',
    letterSpacing: '0.01em',
  },
})

export const font15Bold = mkFont({
  name: '15Bold',
  fontFace: plexBold,
  cssProperties: {
    fontSize: '15px',
    lineHeight: '20px',
    letterSpacing: '0.01em',
  },
})

export const font14Regular = mkFont({
  name: '14Regular',
  fontFace: plexRegular,
  cssProperties: {
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0.01em',
  },
})

export const font14Bold = mkFont({
  name: '14Bold',
  fontFace: plexBold,
  cssProperties: {
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0.01em',
  },
})

export const font12Regular = mkFont({
  name: '12Regular',
  fontFace: plexRegular,
  cssProperties: {
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.01em',
  },
})

export const font11Regular = mkFont({
  name: '11Regular',
  fontFace: plexRegular,
  cssProperties: {
    fontSize: '11px',
    lineHeight: '14px',
    letterSpacing: '0.01em',
  },
})

export const formats = {
  addPlusInteger: (v: number) => (v === 0 ? '0' : v > 0 ? `+${v}` : `${v}`),
}
